/**
 * The access list.
 *
 * The directory says who someone is; this dialog says whether they may come
 * in and what they may do. Adding a row here is what opens the door — which
 * is why the account is picked from a directory-fed dropdown rather than
 * typed: a mistyped username would create an entry matching nobody, and the
 * person would keep being turned away with no clue why.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DirectoryEntry, UserAccount, UserListResponse } from '@proxmon/shared';
import { useStore } from '../state/store.ts';

/** Below this, a search box is clutter; above it, the dropdown is unusable. */
const SEARCH_THRESHOLD = 15;

export function UsersDialog({ onClose }: { onClose: () => void }) {
  const listUsers = useStore((s) => s.listUsers);
  const listDirectory = useStore((s) => s.listDirectory);
  const addUser = useStore((s) => s.addUser);
  const setUserRole = useStore((s) => s.setUserRole);
  const setUserEnabled = useStore((s) => s.setUserEnabled);
  const removeUser = useStore((s) => s.removeUser);
  const testDirectory = useStore((s) => s.testDirectory);
  const notify = useStore((s) => s.notify);
  const me = useStore((s) => s.session?.username);

  const [data, setData] = useState<UserListResponse | null>(null);
  const [candidates, setCandidates] = useState<DirectoryEntry[] | null>(null);
  const [directoryReason, setDirectoryReason] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'viewer'>('viewer');
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const fail = useCallback(
    (err: unknown, fallback: string) => notify('error', err instanceof Error ? err.message : fallback),
    [notify],
  );

  const reload = useCallback(async () => {
    try {
      setData(await listUsers());
    } catch (err) {
      fail(err, 'Could not read the access list.');
    }
    try {
      const directory = await listDirectory();
      if (directory.available) {
        setCandidates(directory.users);
        setTruncated(Boolean(directory.truncated));
        setDirectoryReason(null);
      } else {
        // Falling back to a typed username keeps the dialog usable while the
        // domain controller is down, instead of blocking on it.
        setCandidates(null);
        setDirectoryReason(directory.reason ?? 'unknown reason');
      }
    } catch (err) {
      setCandidates(null);
      setDirectoryReason(err instanceof Error ? err.message : 'unknown reason');
    }
  }, [listUsers, listDirectory, fail]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Matches on the display name as well as the login: no telling which of the
  // two the person doing the adding has in mind.
  const matches = useMemo(() => {
    if (!candidates) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter(
      (c) => c.displayName.toLowerCase().includes(needle) || c.username.toLowerCase().includes(needle),
    );
  }, [candidates, search]);

  // One candidate left: preselect it rather than make them open the dropdown.
  useEffect(() => {
    if (matches.length === 1 && matches[0]) setPick(matches[0].username);
    else if (!matches.some((c) => c.username === pick)) setPick('');
  }, [matches, pick]);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (err) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const submitAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const username = candidates ? pick : typedName.trim();
    if (!username) {
      notify('error', candidates ? 'Pick an account from the list.' : 'Type a username.');
      return;
    }
    const chosen = candidates?.find((c) => c.username === username);
    await run(async () => {
      // The directory's name and email travel with the row, so it reads as a
      // person straight away instead of a bare login until they first sign in.
      await addUser(username, newRole, chosen?.displayName, chosen?.email);
      setPick('');
      setTypedName('');
      setSearch('');
    }, 'Could not add that account.');
  };

  const runTest = async () => {
    setTestResult('Testing…');
    try {
      const result = await testDirectory();
      setTestResult(
        result.ok
          ? `Directory reachable — ${result.server}, base ${result.baseDn}, ${result.sample} account(s) in the sample.`
          : `Failed: ${result.error ?? 'unknown error'}`,
      );
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : 'Could not reach the server.');
    }
  };

  const users = data?.users ?? [];

  return (
    <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog users-dialog" role="dialog" aria-modal="true" aria-label="Who may sign in">
        <header>
          <h2>Who may sign in</h2>
        </header>

        <div className="body">
          <p className="lede">
            {data?.autoCreate ? (
              <>
                The directory checks the password; rights are set here.{' '}
                <strong>Any directory account may sign in</strong> and is added as a viewer on first use.
              </>
            ) : (
              <>
                The directory checks the password; access and rights are decided here.{' '}
                <strong>Only the accounts below may sign in</strong> — being in the directory is not enough.
              </>
            )}
          </p>

          {testResult && <div className="hint test-result">{testResult}</div>}

          <table className="users-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Role</th>
                <th>Last seen</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    Nobody is on the list yet — add someone below to let them in.
                  </td>
                </tr>
              )}
              {users.map((user) => (
                <UserRow
                  key={user.username}
                  user={user}
                  isMe={Boolean(me) && (user.displayName === me || user.username === me)}
                  busy={busy}
                  onRole={(role) => run(() => setUserRole(user.username, role), 'Could not change that role.')}
                  onEnabled={(enabled) =>
                    run(() => setUserEnabled(user.username, enabled), 'Could not change that account.')
                  }
                  onRemove={() => {
                    const ok = window.confirm(
                      `Remove ${user.username}?\n\nTheir directory account is untouched, but they will no longer be able to sign in to Proxmon.`,
                    );
                    if (ok) void run(() => removeUser(user.username), 'Could not remove that account.');
                  }}
                />
              ))}
            </tbody>
          </table>

          <form className="add-user" onSubmit={submitAdd}>
            {candidates && candidates.length > SEARCH_THRESHOLD && (
              <input
                className="search-field"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search a name or a username…"
                spellCheck={false}
                aria-label="Search the directory"
              />
            )}

            <div className="row">
              <label className="grow">
                <span>Add a directory account</span>
                {candidates ? (
                  <select value={pick} onChange={(event) => setPick(event.target.value)} disabled={busy}>
                    <option value="">
                      {matches.length === 0
                        ? 'No account matches'
                        : search
                          ? `— ${matches.length} match(es), pick one —`
                          : '— pick an account —'}
                    </option>
                    {matches.map((c) => (
                      <option key={c.username} value={c.username}>
                        {c.displayName ? `${c.displayName} (${c.username})` : c.username}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                    placeholder="directory username"
                    spellCheck={false}
                    disabled={busy}
                  />
                )}
              </label>

              <label>
                <span>Role</span>
                <select
                  value={newRole}
                  onChange={(event) => setNewRole(event.target.value as 'admin' | 'viewer')}
                  disabled={busy}
                >
                  <option value="viewer">Viewer</option>
                  <option value="admin">Administrator</option>
                </select>
              </label>

              <button type="submit" className="btn primary" disabled={busy}>
                Add
              </button>
              <button type="button" className="btn ghost" onClick={() => void runTest()} disabled={busy}>
                Test directory
              </button>
            </div>

            <div className="hint">
              {directoryReason ? (
                <>
                  Directory unavailable ({directoryReason}) — type the username instead. It has to match the
                  directory exactly.
                </>
              ) : (
                <>
                  {candidates?.length ?? 0} directory account(s) not yet on the list
                  {truncated && <strong> — list truncated, the directory is larger than we read</strong>}. Adding
                  someone here is what lets them in.
                </>
              )}
            </div>
          </form>
        </div>

        <div className="foot">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function UserRow({
  user,
  isMe,
  busy,
  onRole,
  onEnabled,
  onRemove,
}: {
  user: UserAccount;
  isMe: boolean;
  busy: boolean;
  onRole: (role: 'admin' | 'viewer') => void;
  onEnabled: (enabled: boolean) => void;
  onRemove: () => void;
}) {
  return (
    <tr className={user.enabled ? undefined : 'disabled'}>
      <td>
        <strong>{user.displayName || user.username}</strong>
        <span className="sub">
          {user.username}
          {user.source === 'local' && <em className="tag"> local</em>}
          {isMe && <em className="tag"> you</em>}
        </span>
      </td>
      <td>
        {user.source === 'local' ? (
          // The break-glass account is an administrator by definition; making
          // it demotable would just be a way to lock the door from outside.
          <span className="pill">admin</span>
        ) : (
          <>
            <select
              value={user.role}
              disabled={busy || user.roleLocked}
              onChange={(event) => onRole(event.target.value as 'admin' | 'viewer')}
              aria-label={`Role for ${user.username}`}
            >
              <option value="viewer">Viewer</option>
              <option value="admin">Administrator</option>
            </select>
            {user.roleLocked && (
              <span className="tag lock" title="Pinned to admin by PROXMON_LDAP_BOOTSTRAP_ADMINS; change it there">
                env
              </span>
            )}
          </>
        )}
      </td>
      <td className="dim">
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'never'}
      </td>
      <td className="actions">
        <button
          type="button"
          className="btn ghost small"
          disabled={busy}
          onClick={() => onEnabled(!user.enabled)}
          title={user.enabled ? 'Suspend this account' : 'Restore this account'}
        >
          {user.enabled ? 'Suspend' : 'Restore'}
        </button>
        {user.source !== 'local' && (
          <button type="button" className="btn ghost small danger" disabled={busy} onClick={onRemove}>
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}
